import { DropZone, FieldLabel } from "@measured/puck";
import React, { useState } from "react";
import RichTextEditor from "../RichTextEditor";
import {
  alignField,
  borderFields,
  spacingFields,
  boxFields,
  headingTagField,
  headingSizeField,
  fontWeightField,
} from "./puck.tailwind.classes";
import { uploadPuckImage } from "../../pages/tools/services/api";

// Static map keeps Tailwind able to discover every utility class in the
// source — `text-${align}` would be skipped by the JIT scanner.
const TEXT_ALIGN_CLASS = {
  start: "text-start",
  center: "text-center",
  end: "text-end",
};
function alignToClass(value) {
  return TEXT_ALIGN_CLASS[value] || TEXT_ALIGN_CLASS.start;
}

// Grids collapse to a single column on phones so cards stay readable. Map
// is defined statically so Tailwind v4 picks up every variant — the public
// site mirrors this exact map.
const GRID_COLS_CLASS = {
  1: "grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-4",
  5: "grid-cols-1 sm:grid-cols-2 md:grid-cols-5",
  6: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6",
  7: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7",
  8: "grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-8",
  9: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-9",
  10: "grid-cols-1 sm:grid-cols-2 md:grid-cols-5 lg:grid-cols-10",
  11: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-11",
  12: "grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-12",
};
function gridColsClass(columns) {
  const safe = Math.max(1, Math.min(12, Math.round(columns || 1)));
  return GRID_COLS_CLASS[safe] || GRID_COLS_CLASS[1];
}

// Treat values that already look like markup as HTML; everything else (legacy
// plain-text descriptions) gets escaped + wrapped in a paragraph so existing
// cards keep rendering the way they always did.
function descriptionToHtml(description) {
  if (!description) return "";
  if (/<[a-z][^>]*>/i.test(description)) return description;
  const escaped = description
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<p>${escaped.replace(/\n/g, "<br />")}</p>`;
}
function ImageUploadField({ field, onChange, value }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (event) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const { url } = await uploadPuckImage(file);
      onChange(url);
    } catch (err) {
      setError(err?.message || "Erro ao enviar imagem");
    } finally {
      setUploading(false);
    }
  };

  const isUrl = typeof value === "string" && /^https?:\/\//i.test(value);

  return (
    <FieldLabel label={field.label}>
      <input
        type="url"
        placeholder="Cole uma URL ou envie um arquivo abaixo"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={uploading}
        style={{ width: "100%", marginBottom: 8 }}
      />
      <input
        type="file"
        accept="image/*"
        onChange={handleFile}
        disabled={uploading}
      />
      {uploading ? (
        <p style={{ marginTop: 4, fontSize: 12 }}>Enviando…</p>
      ) : isUrl ? (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img
          src={value}
          alt=""
          style={{
            marginTop: 8,
            maxWidth: "100%",
            maxHeight: 120,
            border: "1px solid #ddd",
            borderRadius: 4,
          }}
        />
      ) : null}
      {error ? (
        <p style={{ marginTop: 4, fontSize: 12, color: "#c00" }}>{error}</p>
      ) : null}
    </FieldLabel>
  );
}

export const config = {
  root: {
    fields: {
      title: {
        type: "text",
      },
    },
  },
  // Define the components users can drag and drop in the editor
  components: {
    Heading: {
      // Define the editable fields for the component
      fields: {
        title: {
          type: "text",
        },
        tag: headingTagField,
        size: headingSizeField,
      },
      defaultProps: {
        title: "Title",
        tag: "h1",
        size: "text-2xl",
      },
      // Render the component with the field values
      render: ({ title, tag, size }) => {
        return tag && tag === "h1" ? (
          <h1 className={`${size} `}>{title}</h1>
        ) : tag && tag === "h2" ? (
          <h2 className={`${size} `}>{title}</h2>
        ) : tag && tag === "h3" ? (
          <h3 className={`${size} `}>{title}</h3>
        ) : tag && tag === "h4" ? (
          <h4 className={`${size} `}>{title}</h4>
        ) : tag && tag === "h5" ? (
          <h5 className={`${size} `}>{title}</h5>
        ) : tag && tag === "h6" ? (
          <h6 className={`${size} `}>{title}</h6>
        ) : (
          <h1 className={`${size} `}>{title}</h1>
        );
      },
    },
    Paragraph: {
      // Define the editable fields for the component
      fields: {
        text: {
          type: "textarea",
        },
        textAlign: alignField,
        size: headingSizeField,
        fontWeight: fontWeightField,
        ...spacingFields,
      },
      defaultProps: {
        text: "",
        textAlign: "start",
        size: "",
        fontWeight: "",
        padding: "",
        paddingX: "",
        paddingY: "",
        margin: "",
        marginX: "",
        marginY: "",
      },

      // Render the component with the field texts
      render: ({
        text,
        textAlign,
        fontWeight,
        size,
        padding,
        paddingX,
        paddingY,
        margin,
        marginX,
        marginY,
      }) => {
        const wrapperClass = [
          padding,
          paddingX,
          paddingY,
          margin,
          marginX,
          marginY,
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <div className={wrapperClass}>
            {text?.split("\n").map((line, index) => (
              <p
                key={index}
                className={`text-${textAlign} ${fontWeight} ${size}`}
              >
                {line}
              </p>
            ))}
          </div>
        );
      },
    },

    Image: {
      fields: {
        src: {
          type: "custom",
          label: "Image src",

          render: (props) => <ImageUploadField {...props} />,
        },
        alt: { type: "text" },
        cssClass: { type: "text" },
        ...borderFields,
        ...spacingFields,
        textAlign: alignField,
      },
      defaultProps: {
        src: "",
        alt: "",
        cssClass: "",
        borderRadius: "",
        borderWidth: "",
        borderColor: "",
        boxShadow: "",
        padding: "",
        margin: "",
        textAlign: "start",
      },
      render: ({
        src,
        alt,
        cssClass,
        borderRadius,
        borderWidth,
        borderColor,
        boxShadow,
        padding,
        paddingX,
        paddingY,
        margin,
        marginX,
        marginY,
        textAlign,
      }) => {
        return (
          <img
            src={src}
            alt={alt}
            className={`max-w-full ${textAlign} ${borderRadius} ${borderWidth} ${borderColor} ${boxShadow} ${padding} ${paddingX || ""} ${paddingY || ""} ${margin} ${marginX || ""} ${marginY || ""} ${cssClass} `}
          />
        );
      },
    },
    Grid: {
      fields: {
        columns: { type: "number", min: 1, max: 12 },
        gap: { type: "number", min: 0, max: 64 },
        padding: { type: "number", min: 0, max: 64 },
      },
      defaultProps: {
        columns: 1,
        gap: 4,
        padding: 4,
      },
      render: ({ columns, gap, padding }) => {
        // Render a Grid DropZone where users are able to drag and drop components
        return (
          <DropZone
            zone="page-grid"
            className={`grid ${gridColsClass(columns)} gap-${gap} p-${padding}`}
          />
        );
      },
    },
    Card: {
      // Add the fields for the title, description and padding
      fields: {
        title: { type: "text" },
        titleAlign: { ...alignField, label: "Alinhamento do título" },
        description: {
          type: "custom",
          label: "Descrição",
          render: ({ field, onChange, value }) => (
            <FieldLabel label={field.label}>
              <RichTextEditor
                value={value || ""}
                onChange={onChange}
                placeholder="Escreva a descrição. Use a barra para listas, negrito, etc."
              />
            </FieldLabel>
          ),
        },
        descriptionAlign: { ...alignField, label: "Alinhamento da descrição" },
        cssClass: { type: "text" },
        ...boxFields,
        ...borderFields,
        ...spacingFields,
      },
      // Add default values for each field
      defaultProps: {
        title: "Title",
        titleAlign: "start",
        description: "",
        descriptionAlign: "start",
        padding: 16,
        cssClass: "",
        backgroundColor: "inherit",
      },
      render: ({
        title,
        titleAlign,
        description,
        descriptionAlign,
        padding,
        paddingX,
        paddingY,
        backgroundColor,
        textAlign,
        borderRadius,
        borderWidth,
        borderColor,
        boxShadow,
        margin,
        marginX,
        marginY,
        cssClass,
      }) => {
        // Render the card using the values from its fields
        return (
          <article
            className={`${backgroundColor} ${textAlign} ${borderRadius} ${borderWidth} ${borderColor} ${boxShadow} ${padding} ${paddingX || ""} ${paddingY || ""} ${margin} ${marginX || ""} ${marginY || ""} ${cssClass} `}
            style={typeof padding === "number" ? { padding } : undefined}
          >
            <h2 className={`text-xl font-bold ${alignToClass(titleAlign)}`}>
              {title}
            </h2>
            <div
              className={`card-rich-content ${alignToClass(descriptionAlign)} [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-6 [&_ol]:pl-6 [&_li]:my-1 [&_p]:mb-2 [&_a]:text-blue-600 [&_a]:underline [&_strong]:font-bold [&_em]:italic`}
              dangerouslySetInnerHTML={{ __html: descriptionToHtml(description) }}
            />
          </article>
        );
      },
    },
  },
};
